/**
 * src/components/poSplitTag.js
 *
 * "4/6 lines · 2 on Other" — marks a PO row on the third-party tabs when some of
 * the PO's lines are coded to a cost type another tab owns, so the PO only
 * shows in part here (see helpers/poLineSplit.js).
 *
 *   usePoSplitRow(node, tab)   – how the PO on a tree row splits, or null
 *   <PoSplitTag split />       – the line count and one chip per other tab;
 *                                a chip opens that tab
 */
import React, { useState } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { jobCostDetailAtom, jobOpenCommitmentsAtom, selectedTab } from '../store/company';
import { fNum, setSelectedTabStrorage } from '../db/company';
import { findSplitPos, splitForTab } from '../helpers/poLineSplit';
import { poKey } from '../helpers/reversedPos';

// Every row reads the same result, so compute it once per cost/OC load.
let splitCache = { ch: undefined, oc: undefined, split: new Map() };
const useSplitPos = () => {
    const [costRows] = useAtom(jobCostDetailAtom);
    const [ocRows] = useAtom(jobOpenCommitmentsAtom);
    if (splitCache.ch !== costRows || splitCache.oc !== ocRows) {
        splitCache = { ch: costRows, oc: ocRows, split: findSplitPos(costRows, ocRows) };
    }
    return splitCache.split;
};

export const usePoSplitRow = (node, tab, skip) => {
    const split = useSplitPos();
    if (skip || !tab || !split.size || node?.data?.path !== 'PO_Number') return null;
    const entry = split.get(poKey(node.data.key));
    return entry ? splitForTab(entry, tab) : null;
};

// Room the tag takes at the right of the title cell, so the PO number's
// ellipsis stops short of it.
export const poSplitTagWidth = (split) => (split ? 56 + split.elsewhere.length * 74 : 0);

const lineList = (lines) => lines.map((l) => l.line).join(', ');

const chipTooltip = (split, group) => {
    const n = group.lines.length;
    const cts = [...new Set(group.lines.flatMap((l) => [...l.costTypes]))];
    const desc = group.lines.find((l) => l.costTypeDescription)?.costTypeDescription;
    const coded = cts.length === 1 ? `${cts[0]}${desc ? ' ' + desc : ''}` : cts.join(', ');
    const where = group.tab ? `so ${n === 1 ? 'it shows' : 'they show'} on the ${group.label} tab` : 'which no third-party tab shows';
    const list = group.lines.slice(0, 8)
        .map((l) => `  ${l.line}  ${l.description || ''}${l.amount ? '  $' + fNum(l.amount) : ''}`)
        .join('\n');
    const more = group.lines.length > 8 ? `\n  …and ${group.lines.length - 8} more` : '';
    const lead = `PO ${split.po} has ${split.total} lines; ${split.here} show on this tab.\n`
        + `${n === 1 ? 'Line' : 'Lines'} ${lineList(group.lines)} ${n === 1 ? 'is' : 'are'} coded ${coded} in JDE, ${where}:`;
    return `${lead}\n${list}${more}${group.tab ? `\nClick to open ${group.label}.` : ''}`;
};

const chipBase = {
    height: 18, paddingLeft: 7, paddingRight: 7, borderRadius: 9999,
    flexDirection: 'row', alignItems: 'center', flexShrink: 0,
};

// One chip that opens another tab. Pressable renders a div, which the global
// focus rule in strike.css does not reach, so it draws its own focus ring.
const TabChip = ({ label, title, tabLabel, onOpen }) => {
    const [focusRing, setFocusRing] = useState(false);
    return (
        <Pressable
            title={title}
            role="button"
            tabIndex={0}
            aria-label={`Open the ${tabLabel} tab: ${label}`}
            onPress={(e) => { e?.stopPropagation?.(); onOpen(); }}
            onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault(); e.stopPropagation(); onOpen();
            }}
            onFocus={(e) => setFocusRing(!!e.currentTarget.matches?.(':focus-visible'))}
            onBlur={() => setFocusRing(false)}
            style={({ hovered }) => [chipBase, {
                outline: 'none',
                boxShadow: focusRing ? 'var(--focus-ring)' : 'none',
                transition: 'background-color .15s, box-shadow .15s',
                backgroundColor: hovered ? 'color-mix(in srgb, var(--primary) 16%, var(--primary-tint))' : 'var(--primary-tint)',
            }]}
        >
            <Text style={{ fontSize: 11, fontWeight: '500', color: 'var(--primary)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{label}</Text>
        </Pressable>
    );
};

export const PoSplitTag = ({ split, style }) => {
    const [, setTab] = useAtom(selectedTab);
    if (!split) return null;
    const open = (tab) => {
        setTab(tab);
        setSelectedTabStrorage(tab);
    };
    return (
        <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 6 }, style]}>
            <Text
                title={`PO ${split.po} has ${split.total} lines; ${split.here} show on this tab.`}
                style={{ fontSize: 11, fontWeight: '500', color: 'var(--muted-foreground)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            >
                {split.here}/{split.total} lines
            </Text>
            {split.elsewhere.map((g) => {
                const label = `${g.lines.length} on ${g.label}`;
                const title = chipTooltip(split, g);
                return g.tab ? (
                    <TabChip key={g.tab} label={label} title={title} tabLabel={g.label} onOpen={() => open(g.tab)} />
                ) : (
                    <View key={g.label} title={title} style={[chipBase, { backgroundColor: 'var(--muted)' }]}>
                        <Text style={{ fontSize: 11, fontWeight: '500', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{label}</Text>
                    </View>
                );
            })}
        </View>
    );
};
